import {
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { chatSettings } from "./schemas/chat-settings";
import { conversations } from "./schemas/conversations";
import { messages } from "./schemas/messages";

// ============================================================================
// Chat module — registered doctypes.
//
// Phase 0E of #644 is metadata-only. Chat conversation/message writes remain
// route/service owned because inbound webhooks, outbound sends, and durable
// object state have channel-specific invariants.
// ============================================================================

const CONVERSATION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const MESSAGE_CATALOG: DoctypeCatalogMetadata = {
  childTable: {
    parentDoctype: "conversation",
    parentField: "messages",
    parentKeyField: "conversation_id",
    sortField: "created_at",
    cascadePolicy: "restrict",
  },
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const SETTINGS_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: false,
    isExportable: true,
    isReportable: false,
  },
};

export const conversationDoctype = defineDoctype({
  id: "conversation",
  moduleId: "chat",
  table: conversations,
  label: "บทสนทนา",
  icon: "MessageCircle",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: CONVERSATION_CATALOG,
  displayField: "sender_name",
  imageField: "sender_avatar",
  searchFields: ["external_user_id", "sender_name", "status", "last_message_preview"],
  columnLabels: {
    company_id: "บริษัท",
    connector_instance_id: "การเชื่อมต่อ",
    channel_type: "ช่องทาง",
    external_user_id: "รหัสผู้ใช้ภายนอก",
    sender_name: "ชื่อผู้ส่ง",
    sender_avatar: "รูปผู้ส่ง",
    status: "สถานะ",
    assigned_to: "ผู้รับผิดชอบ",
    last_message_at: "ข้อความล่าสุดเมื่อ",
    last_message_preview: "ตัวอย่างข้อความล่าสุด",
    unread_count: "จำนวนยังไม่อ่าน",
    ai_agent_id: "AI Agent",
    metadata: "ข้อมูลเพิ่มเติม",
  },
});

export const messageDoctype = defineDoctype({
  id: "message",
  moduleId: "chat",
  table: messages,
  label: "ข้อความ",
  icon: "MessageSquare",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: MESSAGE_CATALOG,
  displayField: "content",
  searchFields: ["content", "provider_message_id", "sender_type"],
  columnLabels: {
    conversation_id: "บทสนทนา",
    direction: "ทิศทาง",
    sender_type: "ประเภทผู้ส่ง",
    content: "ข้อความ",
    message_type: "ประเภทข้อความ",
    provider_message_id: "รหัสข้อความจากผู้ให้บริการ",
    metadata: "ข้อมูลเพิ่มเติม",
  },
});

export const chatSettingsDoctype = defineDoctype({
  id: "chat_settings",
  moduleId: "chat",
  table: chatSettings,
  label: "ตั้งค่าแชท",
  icon: "Settings",
  features: ["permissions"],
  tier: 2,
  kind: "single",
  catalog: SETTINGS_CATALOG,
  displayField: "company_id",
  columnLabels: {
    company_id: "บริษัท",
    default_system_prompt: "System Prompt เริ่มต้น",
    default_ai_mode_enabled: "ให้ AI ตอบเป็นค่าเริ่มต้น",
    handoff_message: "ข้อความโอนสาย",
    welcome_message: "ข้อความต้อนรับ",
  },
});

export const chatDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  conversationDoctype,
  messageDoctype,
  chatSettingsDoctype,
]);
